// Vault hosted-page template — baked-in sample content.
// Personal training studio sample (Summit). Image paths are namespaced under
// /vault-template/images/ to avoid collisions with the rest of the app's public
// assets. Data-wiring (onboarding/campaign) is deferred to a later task — this
// is a design-perfection replica only.

// The individual training offerings. Rendered as rotating slides inside the
// "Our Services" item of the What We Do section.
const serviceOfferings = [
  {
    title: "1-on-1 Personal Training",
    description:
      "Just you and your coach. Every session is built around your goals, your body, and how you're moving that day.",
    image: "/vault-template/images/fit-service-1.jpg",
  },
  {
    title: "Small Group Training",
    description:
      "Train alongside a handful of people chasing the same thing. Coached, programmed, and capped small so you're never just a number.",
    image: "/vault-template/images/fit-service-2.jpg",
  },
  {
    title: "Strength & Conditioning",
    description:
      "Get genuinely stronger with structured programming that builds power, mobility, and stamina - no random workouts.",
    image: "/vault-template/images/fit-service-3.jpg",
  },
  {
    title: "Nutrition Coaching",
    description:
      "Training is only half of it. We help you fuel your results with a sustainable plan that fits your life - not a crash diet.",
    image: "/vault-template/images/fit-service-4.jpg",
  },
];

// The studio tour photos. Used both by the standalone Project Gallery section
// and as rotating slides inside the "Take a Tour" item of What We Do.
const studioGallery = [
  {
    id: 1,
    title: "The Training Floor",
    description:
      "Wide-open functional space with machines, a rig, and a heavy bag - room to move through any workout without waiting on a station.",
    image: "/vault-template/images/fit-studio-1.jpg",
  },
  {
    id: 2,
    title: "The Strength Floor",
    description:
      "Racked plates and a full set of kettlebells, ready to go. Whether it's your first lift or your heaviest, this is home base.",
    image: "/vault-template/images/fit-studio-2.jpg",
  },
  {
    id: 3,
    title: "The Cycle Studio",
    description:
      "A full row of indoor bikes for high-energy group rides. Clip in, find your rhythm, and let the class push you further.",
    image: "/vault-template/images/fit-studio-3.jpg",
  },
  {
    id: 4,
    title: "The Cardio Deck",
    description:
      "Treadmills and rowers lined up along floor-to-ceiling windows. Get your conditioning in with a view instead of a wall.",
    image: "/vault-template/images/fit-studio-4.jpg",
  },
];

export const siteConfig = {
  // --- Company Info ---
  companyName: "Summit Personal Training",
  companyNameShort: "SUMMIT.",
  phone: "555-867-5309",
  phoneLink: "+15558675309",
  address: {
    street: "1600 Pennsylvania Ave.",
    city: "Washington",
    state: "DC",
    zip: "20500",
    latitude: "38.8977",
    longitude: "-77.0365",
  },
  website: "https://summitpt.com",

  // --- Social Links ---
  socialLinks: {
    instagram: "https://instagram.com",
    facebook: "https://facebook.com",
    linkedin: "https://linkedin.com",
  },

  // --- Ratings (shown in hero card) ---
  ratings: {
    score: "4.9",
    count: "87",
    source: "Google Reviews",
  },

  // --- SEO & Open Graph ---
  seo: {
    title: "Summit Personal Training | DC's Premier Personal Training Studio",
    description:
      "DC's personal training studio for 1-on-1 coaching, small-group training, and real results. Certified coaches, personalized programs, zero guesswork.",
    ogDescription:
      "We build strength that changes how you live. DC's personal training studio for 1-on-1 coaching and small-group training.",
  },

  // --- Business Hours (used in JSON-LD schema) ---
  businessHours: {
    days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    open: "05:00",
    close: "21:00",
  },

  // --- Theme Colors ---
  theme: {
    accent: "#F4AB15",
    // Accent for the DARK sections (hero + footer). The design-preview route
    // falls back to the same default; the live hosted page swaps in the owner's
    // dark-section accent (business-profile accentColorDark).
    accentDark: "#F4AB15",
    // Pure black default (July 2026 — replaced the deep navy #111827). Hero +
    // footer share this default; owners can still override via the color picker.
    darkBg: "#000000",
    darkSurface: "#0A0A0A",
    creamBg: "#000000",
    // Background for the middle "What We Do" / Services section (Hero and footer
    // stay black). Kept separate from creamBg so the footer color can change
    // independently without dragging the middle section with it.
    sectionBg: "#FFFFFF",
    middleDefaultBg: "#FCFBF8",
    darkText: "#000000",
  },

  // --- Hero Section ---
  // The hero is now a button-driven segmented video. `media` is the resting
  // poster/preview shown before any button is pressed (an image OR a self-hosted
  // video, detected by file extension). `segments` maps each hero action to the
  // clip that plays when its button is pressed:
  //   intro   — the Play button on the poster
  //   pricing — the "Get Pricing" button
  //   message — the "Send a Message" button
  // These are placeholder talking clips; swap each path to drop in real footage.
  hero: {
    media: "/vault-template/videos/hero-intro.mp4",
    segments: {
      intro: "/vault-template/videos/hero-intro.mp4",
      pricing: "/vault-template/videos/hero-pricing.mp4",
      message: "/vault-template/videos/hero-message.mp4",
    },
  },

  // --- Services Section ---
  services: {
    label: "What to Expect",
    titleBold: "Get to Know Us.",
    titleLight: "",
    description:
      "Every program starts with a conversation. What are you training for? What's worked, what hasn't, what do you actually enjoy? We figure that out first - and build your program from there.",
    items: [
      {
        number: "01",
        title: "Our Services",
        description: serviceOfferings[0].description,
        image: serviceOfferings[0].image,
        // Rotating slides — one per training offering.
        slides: serviceOfferings,
      },
      {
        number: "02",
        title: "Take a Tour",
        description: studioGallery[0].description,
        image: studioGallery[0].image,
        // Rotating slides — one per studio gallery photo.
        slides: studioGallery.map(({ image, title, description }) => ({ image, title, description })),
      },
    ],
  },

  // --- Testimonials ---
  // First testimonial is displayed large/featured. Entries with a showcaseImage
  // render the alternating quote + project-photo layout with click-to-expand.
  testimonials: [
    {
      quote:
        "I joined to keep up with my kids on the trail, and now I'm the one setting the pace. Best decision our whole family has made.",
      name: "Marcus Bennett",
      detail: "Strength & Conditioning",
      showcaseImage: "/vault-template/images/review-family.jpg",
      videoUrl: "/vault-template/videos/testimonial-marcus.mp4",
    },
    {
      quote:
        "Eight weeks postpartum I didn't recognize my body. My coach built the plan around recovery first - I got strong again without ever feeling rushed.",
      name: "Priya Anand",
      detail: "Postnatal Coaching",
      showcaseImage: "/vault-template/images/review-couple.jpg",
    },
    {
      quote:
        "The three of us signed up together on a dare. A year later it's the best part of our week, and we've never been fitter.",
      name: "Jasmine Carter",
      detail: "Small Group Training",
      showcaseImage: "/vault-template/images/review-friends.jpg",
    },
    {
      quote:
        "I went from never riding to finishing my first sprint triathlon. The cycle studio coaching turned the bike leg into my strongest.",
      name: "Elena Rossi",
      detail: "Cycle Studio, Westlake Hills",
      showcaseImage: "/vault-template/images/review-cyclist.jpg",
      videoUrl: "/vault-template/videos/testimonial-elena.mp4",
    },
    {
      quote:
        "At 52 I thought my racing days were behind me. My coach rebuilt my training around my joints, and I just crossed the finish line of the Seoul Marathon.",
      name: "David Park",
      detail: "Endurance & Marathon Prep",
      showcaseImage: "/vault-template/images/review-runner.jpg",
    },
  ],

  // --- Team / Owner Section ---
  team: {
    label: "Meet the Team",
    heading: "The Coaches Behind Your Results.",
    subheading:
      "We're a small team of certified coaches who take your progress personally.",
    featured: {
      name: "Mike Sullivan",
      role: "Founder & Head Coach",
      image: "/vault-template/videos/team-mike.mp4",
      bio: "With 15+ years of coaching behind him, Mike personally designs every member's program. No cookie-cutter plans, ever — just training built around your goals and how your body actually moves.",
    },
    members: [
      {
        name: "Tom Rivera",
        role: "Strength Coach",
        image: "/vault-template/images/team-1.png",
        bio: "A former competitive powerlifter with 10+ years coaching strength athletes, Tom specializes in safe, progressive overload that builds real, lasting strength without the guesswork.",
      },
      {
        name: "Sarah Kim",
        role: "Mobility Coach",
        image: "/vault-template/images/team-2.png",
        bio: "Certified in movement and recovery therapy, Sarah helps members train pain-free and move better. She's your go-to for everything mobility and flexibility.",
      },
      {
        name: "James Okafor",
        role: "Performance Coach",
        image: "/vault-template/videos/team-james.mp4",
        bio: "James builds speed, power, and conditioning for athletes of every level, with programming focused on real-world athletic performance you can feel in and out of the gym.",
      },
      {
        name: "Maria Lopez",
        role: "Nutrition Coach",
        image: "/vault-template/images/team-4.png",
        bio: "A registered dietitian, Maria builds sustainable, no-fad meal plans focused on long-term body composition — fuel that actually fits your life instead of fighting it.",
      },
      {
        name: "Dev Patel",
        role: "Personal Trainer",
        image: "/vault-template/images/team-5.png",
        bio: "NASM-certified and great with busy professionals, Dev keeps things simple with habit-based 1:1 coaching that works around your schedule, not the other way around.",
      },
      {
        name: "Grace Lee",
        role: "Member Experience",
        image: "/vault-template/images/team-6.png",
        bio: "Grace is your first point of contact — leading onboarding and accountability, and making sure your program stays on track from your very first session onward.",
      },
    ],
  },

  // --- Owner Info ---
  owner: {
    name: "Mike",
    role: "head coach",
    pronounSubject: "He",
    pronounObject: "him",
    notificationEmail: "mike@summitpt.com",
  },

  // --- Contact Section ---
  contact: {
    heading: "Ready to Start?",
    subheading: "Let's talk about your goals.",
    body: "Whether you're just getting back into it or chasing a specific goal, we'll talk through where you're at and figure out the right plan together.",
  },

  // --- FAQ Section ---
  faqs: [
    {
      question: "How long until I see results?",
      answer:
        "It depends on your starting point and goals, but most members feel stronger and more energized within 4–6 weeks, with visible changes by 8–12 weeks. We track your progress so you can see it happening, not just hope for it.",
      image: "/vault-template/images/faq-1.png",
    },
    {
      question: "Do I need to be fit before I start?",
      answer:
        "Not at all. Most people start with us precisely because they're not where they want to be. Every program is scaled to your current level and built up from there - no judgment, no keeping up with anyone else.",
      image: "/vault-template/images/faq-2.png",
    },
    {
      question: "Do you offer a free consultation?",
      answer:
        "Yes. Your first session is always a free consultation and movement assessment. We'll talk through your goals, see how you move, and map out a plan that fits your body and your schedule.",
      image: "/vault-template/images/faq-3.png",
    },
    {
      question: "What if I have an injury or limitation?",
      answer:
        "We work around injuries and limitations all the time. Your coach builds your program around what your body can do safely, and we adjust as you get stronger. If you're unsure, just check with your doctor first.",
      image: "/vault-template/images/faq-4.png",
    },
  ],

  // --- Navigation ---
  nav: [
    { label: "What to Expect", href: "#services" },
    { label: "Reviews", href: "#client-stories" },
  ],
};

export type SiteConfig = Omit<typeof siteConfig, 'ratings' | 'testimonials' | 'services' | 'team'> & {
  logo?: string;
  ratings: typeof siteConfig.ratings & { googleReviewUrl?: string };
  testimonials: Array<typeof siteConfig.testimonials[number] & { image?: string; images?: string[]; showcaseImage?: string; projectType?: string; videoUrl?: string }>;
  services: Omit<typeof siteConfig.services, 'items'> & {
    items: Array<typeof siteConfig.services.items[number] & {
      image?: string;
      // Optional image+copy slides shown in the right column with prev/next
      // arrows and dot indicators. When present, these ARE the slides (the item's
      // own image/title/description are used only as the fallback when no slides
      // are configured). `title` defaults to the service title.
      slides?: Array<{ image?: string; title?: string; description: string }>;
    }>;
  };
  contact: typeof siteConfig.contact & { cta?: string };
  team?: {
    label?: string;
    heading?: string;
    subheading?: string;
    featured?: {
      name: string;
      role: string;
      bio?: string;
      bullets?: string[];
      image?: string;
    };
    members?: Array<{
      name: string;
      role: string;
      bio?: string;
      bullets?: string[];
      image?: string;
    }>;
  };
};
